import type { DefaultPaymentLogsListParams, DefaultPaymentLogsSortKey } from '../types/pages'
/**
 * Default payment logs — React parity with Angular `ReportsDefaultPaymentLogsComponent`.
 * Uses `GET /api/default_payment_log` so the same filters/session apply as the legacy app,
 * without opening a separate Angular origin (which drops the React auth cookie).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronsUpDown, Download, Eye, Loader2, Pencil, Wallet } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { getReimbursementsListUrlForApplicant } from '@/lib/utils/residentNavigation'
import {
    DefaultPaymentLogsInlineFilterBar,
    type DefaultPaymentLogsFilterValues,
} from '../components/DefaultPaymentLogsInlineFilterBar'
import { ListColumnsMenu } from '@/components/ui/ListColumnsMenu'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { TableSearchFilterEmptyTableRow } from '@/components/ui/TableSearchFilterEmptyState'
import { showApiError } from '@/lib/utils/notifications'
import { formatAmount } from '@/lib/utils/formatters'
import { useConfigurableListColumns } from '@/hooks/useConfigurableListColumns'
import { cn } from '@lib/utils/helpers'
import { ReportOverviewMetrics } from '../components/ReportOverviewMetrics'
import { ReportTableScroll } from '../components/ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'
import { getDefaultPaymentLogList } from '../services/reportsService'
import type { DefaultPaymentLogListResponse, DefaultPaymentLogRow } from '../types/api'
import {
    buildSessionFilterFromListParams,
    clearDefaultPaymentLogsSession,
    readDefaultPaymentLogsSession,
    sessionFilterToListParamsPatch,
    writeDefaultPaymentLogsSession,
} from '../utils/defaultPaymentLogsSessionFilter'
import {
    renderDefaultPaymentLogCell,
    defaultPaymentLogCellClassName,
} from '../utils/defaultPaymentLogCells'
import {
    DEFAULT_DEFAULT_PAYMENT_LOGS_PARAMS as DEFAULT_PARAMS,
    DEFAULT_PAYMENT_LOGS_SORT_COLUMNS as SORT_COLUMNS,
} from '../constants/pages/defaultPaymentLogsPage'
import {
    DEFAULT_PAYMENT_LOGS_COLUMN_OPTIONS,
    DEFAULT_PAYMENT_LOGS_COLUMN_VISIBILITY_STORAGE_KEY,
    DEFAULT_PAYMENT_LOGS_LOCKED_COLUMN_IDS,
} from '../constants/listColumns'

/** Angular sends payment date range as DD-MM-YYYY on the query string. */
function ymdToDmy(ymd: string): string {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
    const [y, m, d] = ymd.split('-')
    return `${d}-${m}-${y}`
}

/** Same query shape as Angular `ReportsDefaultPaymentLogsComponent.getRequestBody` → GET list. */
function buildDefaultPaymentLogQueryParams(p: DefaultPaymentLogsListParams): URLSearchParams {
    const qs = new URLSearchParams()
    qs.set('page', String(p.page))
    qs.set('perPage', String(p.perPage))
    qs.set('sortBy', p.sortBy)
    qs.set('orderBy', p.orderBy)
    if (p.q.trim()) qs.set('q', p.q.trim())
    if (p.company_id) qs.set('company_id', p.company_id)
    if (p.property_id) qs.set('property_id', p.property_id)
    const fromApi = ymdToDmy(p.fromDt)
    const toApi = ymdToDmy(p.toDt)
    if (fromApi) qs.set('fromDt', fromApi)
    if (toApi) qs.set('toDt', toApi)
    if (p.payment_verified === '0' || p.payment_verified === '1') {
        qs.set('payment_verified', p.payment_verified)
    }
    for (const s of p.payment_status) {
        if (s) qs.append('status[]', s)
    }
    if (p.claim_status.trim()) qs.set('claim_status', p.claim_status.trim())
    if (p.vintage_year.trim()) qs.set('vintage_year', p.vintage_year.trim())
    const lsFrom = ymdToDmy(p.lease_start_fromDt)
    const lsTo = ymdToDmy(p.lease_start_toDt)
    if (lsFrom) qs.set('lease_start_fromDt', lsFrom)
    if (lsTo) qs.set('lease_start_toDt', lsTo)
    const csFrom = ymdToDmy(p.claim_status_fromDt)
    const csTo = ymdToDmy(p.claim_status_toDt)
    if (csFrom) qs.set('claim_status_fromDt', csFrom)
    if (csTo) qs.set('claim_status_toDt', csTo)
    if (p.is_reissue_payment === '0' || p.is_reissue_payment === '1') {
        qs.set('is_reissue_payment', p.is_reissue_payment)
    }
    return qs
}

/** Angular export uses indexed `status[0]=`… not repeated `status[]=`. */
function buildDefaultPaymentLogExportSearchParams(p: DefaultPaymentLogsListParams): URLSearchParams {
    const qs = new URLSearchParams()
    qs.set('page', String(p.page))
    qs.set('perPage', String(p.perPage))
    qs.set('sortBy', p.sortBy)
    qs.set('orderBy', p.orderBy)
    if (p.q.trim()) qs.set('q', p.q.trim())
    if (p.company_id) qs.set('company_id', p.company_id)
    if (p.property_id) qs.set('property_id', p.property_id)
    const fromApi = ymdToDmy(p.fromDt)
    const toApi = ymdToDmy(p.toDt)
    if (fromApi) qs.set('fromDt', fromApi)
    if (toApi) qs.set('toDt', toApi)
    if (p.payment_verified === '0' || p.payment_verified === '1') {
        qs.set('payment_verified', p.payment_verified)
    }
    p.payment_status.forEach((s, i) => {
        if (s) qs.set(`status[${i}]`, s)
    })
    if (p.claim_status.trim()) qs.set('claim_status', p.claim_status.trim())
    if (p.vintage_year.trim()) qs.set('vintage_year', p.vintage_year.trim())
    const lsFrom = ymdToDmy(p.lease_start_fromDt)
    const lsTo = ymdToDmy(p.lease_start_toDt)
    if (lsFrom) qs.set('lease_start_fromDt', lsFrom)
    if (lsTo) qs.set('lease_start_toDt', lsTo)
    const csFrom = ymdToDmy(p.claim_status_fromDt)
    const csTo = ymdToDmy(p.claim_status_toDt)
    if (csFrom) qs.set('claim_status_fromDt', csFrom)
    if (csTo) qs.set('claim_status_toDt', csTo)
    if (p.is_reissue_payment === '0' || p.is_reissue_payment === '1') {
        qs.set('is_reissue_payment', p.is_reissue_payment)
    }
    return qs
}

function buildDefaultPaymentLogExportHref(p: DefaultPaymentLogsListParams): string {
    const token = getAuthTokenFromStorage()
    if (!token) return ''
    const qs = buildDefaultPaymentLogExportSearchParams(p)
    qs.set('access_token', token)
    qs.set('view', 'xlsx')
    return `${getBrowserApiBaseUrl()}/api/export/default_payment_log?${qs.toString()}`
}

function initialListParamsFromUrlAndSession(): DefaultPaymentLogsListParams {
    const u = new URLSearchParams(window.location.search)
    const propertyIdFromUrl = u.get('property_id') ?? ''
    if (propertyIdFromUrl) {
        return { ...DEFAULT_PARAMS, property_id: propertyIdFromUrl }
    }
    const session = readDefaultPaymentLogsSession()
    if (session) {
        const { patch } = sessionFilterToListParamsPatch(session)
        return { ...DEFAULT_PARAMS, ...patch }
    }
    return { ...DEFAULT_PARAMS }
}

function initialPropertyNameHintFromUrlAndSession(): string {
    const u = new URLSearchParams(window.location.search)
    const propertyNameFromUrl = u.get('property_name') ?? ''
    if (propertyNameFromUrl) return propertyNameFromUrl
    const session = readDefaultPaymentLogsSession()
    if (!session) return ''
    return sessionFilterToListParamsPatch(session).propertyNameHint
}

export function DefaultPaymentLogsPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const propertyNameFromUrl = searchParams.get('property_name') ?? ''

    const [params, setParams] = useState<DefaultPaymentLogsListParams>(initialListParamsFromUrlAndSession)
    const [rows, setRows] = useState<DefaultPaymentLogRow[]>([])
    const [meta, setMeta] = useState<Pick<DefaultPaymentLogListResponse, 'total' | 'total_payment_count'>>({
        total: 0,
    })
    const [pageOptions, setPageOptions] = useState({ current: 1, last: 1 })
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [propertyNameHint] = useState(initialPropertyNameHintFromUrlAndSession)
    const propertyLabelRef = useRef(
        propertyNameFromUrl || propertyNameHint || '',
    )

    const columnsController = useConfigurableListColumns({
        storageKey: DEFAULT_PAYMENT_LOGS_COLUMN_VISIBILITY_STORAGE_KEY,
        columnOptions: DEFAULT_PAYMENT_LOGS_COLUMN_OPTIONS,
        lockedColumnIds: [...DEFAULT_PAYMENT_LOGS_LOCKED_COLUMN_IDS],
    })
    const visibleColumns = useMemo(() => {
        const byKey = new Map(SORT_COLUMNS.map((column) => [column.key, column]))
        return columnsController.tableColumnIds
            .map((columnId) => byKey.get(columnId as DefaultPaymentLogsSortKey))
            .filter(
                (column): column is (typeof SORT_COLUMNS)[number] => Boolean(column),
            )
    }, [columnsController.tableColumnIds])

    useLayoutEffect(() => {
        propertyLabelRef.current =
            propertyNameFromUrl || propertyNameHint || propertyLabelRef.current
    }, [propertyNameFromUrl, propertyNameHint])

    const load = useCallback(async (next: DefaultPaymentLogsListParams) => {
        setLoading(true)
        try {
            const res = await getDefaultPaymentLogList(buildDefaultPaymentLogQueryParams(next))
            setRows(res.data ?? [])
            setPageOptions({ current: res.current ?? 1, last: Math.max(1, res.last ?? 1) })
            setMeta({ total: res.total ?? 0, total_payment_count: res.total_payment_count })
            writeDefaultPaymentLogsSession(
                buildSessionFilterFromListParams(
                    {
                        page: next.page,
                        perPage: next.perPage,
                        sortBy: next.sortBy,
                        orderBy: next.orderBy,
                        q: next.q,
                        property_id: next.property_id,
                        company_id: next.company_id,
                        fromDt: next.fromDt,
                        toDt: next.toDt,
                        payment_verified: next.payment_verified,
                        payment_status: next.payment_status,
                        claim_status: next.claim_status,
                        vintage_year: next.vintage_year,
                        lease_start_fromDt: next.lease_start_fromDt,
                        lease_start_toDt: next.lease_start_toDt,
                        claim_status_fromDt: next.claim_status_fromDt,
                        claim_status_toDt: next.claim_status_toDt,
                        is_reissue_payment: next.is_reissue_payment,
                    },
                    { propertyDisplayName: propertyLabelRef.current || undefined },
                ),
            )
        } catch (e) {
            showApiError(e)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load(params)
    }, [load, params])

    useEffect(() => {
        setSearchTerm(params.q || '')
    }, [params.q])

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const nextQ = searchTerm.trim()
            if (nextQ !== params.q.trim()) {
                setParams((prev) => ({ ...prev, q: nextQ, page: 1 }))
            }
        }, 500)

        return () => window.clearTimeout(timer)
    }, [searchTerm, params.q])

    const filterValuesForDrawer = useMemo(
        (): DefaultPaymentLogsFilterValues => ({
            company_id: params.company_id || undefined,
            property_id: params.property_id || undefined,
            fromDt: params.fromDt || undefined,
            toDt: params.toDt || undefined,
            payment_verified:
                params.payment_verified === '' ? undefined : params.payment_verified,
            payment_status: params.payment_status.length ? [...params.payment_status] : undefined,
            claim_status: params.claim_status || undefined,
            vintage_year: params.vintage_year || undefined,
            lease_start_fromDt: params.lease_start_fromDt || undefined,
            lease_start_toDt: params.lease_start_toDt || undefined,
            claim_status_fromDt: params.claim_status_fromDt || undefined,
            claim_status_toDt: params.claim_status_toDt || undefined,
            is_reissue_payment:
                params.is_reissue_payment === '' ? undefined : params.is_reissue_payment,
        }),
        [
            params.company_id,
            params.property_id,
            params.fromDt,
            params.toDt,
            params.payment_verified,
            params.payment_status,
            params.claim_status,
            params.vintage_year,
            params.lease_start_fromDt,
            params.lease_start_toDt,
            params.claim_status_fromDt,
            params.claim_status_toDt,
            params.is_reissue_payment,
        ],
    )

    const applyDrawerFilters = useCallback((patch: Partial<DefaultPaymentLogsFilterValues>) => {
        setParams((prev) => ({
            ...prev,
            company_id: 'company_id' in patch ? patch.company_id ?? '' : prev.company_id,
            property_id: 'property_id' in patch ? patch.property_id ?? '' : prev.property_id,
            fromDt: 'fromDt' in patch ? patch.fromDt ?? '' : prev.fromDt,
            toDt: 'toDt' in patch ? patch.toDt ?? '' : prev.toDt,
            payment_verified:
                'payment_verified' in patch
                    ? ((patch.payment_verified ?? '') as DefaultPaymentLogsListParams['payment_verified'])
                    : prev.payment_verified,
            payment_status: 'payment_status' in patch ? patch.payment_status ?? [] : prev.payment_status,
            claim_status: 'claim_status' in patch ? patch.claim_status ?? '' : prev.claim_status,
            vintage_year: 'vintage_year' in patch ? patch.vintage_year ?? '' : prev.vintage_year,
            lease_start_fromDt:
                'lease_start_fromDt' in patch ? patch.lease_start_fromDt ?? '' : prev.lease_start_fromDt,
            lease_start_toDt:
                'lease_start_toDt' in patch ? patch.lease_start_toDt ?? '' : prev.lease_start_toDt,
            claim_status_fromDt:
                'claim_status_fromDt' in patch ? patch.claim_status_fromDt ?? '' : prev.claim_status_fromDt,
            claim_status_toDt:
                'claim_status_toDt' in patch ? patch.claim_status_toDt ?? '' : prev.claim_status_toDt,
            is_reissue_payment:
                'is_reissue_payment' in patch
                    ? ((patch.is_reissue_payment ?? '') as DefaultPaymentLogsListParams['is_reissue_payment'])
                    : prev.is_reissue_payment,
            page: 1,
        }))
    }, [])

    const activeFilterCount = useMemo(
        () =>
            [
                params.company_id,
                params.property_id,
                params.fromDt || params.toDt,
                params.payment_verified,
                params.payment_status.length ? 'payment_status' : '',
                params.claim_status,
                params.vintage_year,
                params.lease_start_fromDt || params.lease_start_toDt,
                params.claim_status_fromDt || params.claim_status_toDt,
                params.is_reissue_payment,
            ].filter(Boolean).length,
        [
            params.company_id,
            params.property_id,
            params.fromDt,
            params.toDt,
            params.payment_verified,
            params.payment_status,
            params.claim_status,
            params.vintage_year,
            params.lease_start_fromDt,
            params.lease_start_toDt,
            params.claim_status_fromDt,
            params.claim_status_toDt,
            params.is_reissue_payment,
        ],
    )

    const sort = (orderBy: DefaultPaymentLogsSortKey) => {
        setParams((prev) => ({
            ...prev,
            sortBy: prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
            orderBy,
            page: 1,
        }))
    }

    const resetFilters = () => {
        setSearchTerm('')
        clearDefaultPaymentLogsSession()
        setSearchParams(new URLSearchParams())
        setParams({ ...DEFAULT_PARAMS })
        propertyLabelRef.current = ''
    }

    const initialPropertyOption = useMemo(() => {
        if (!params.property_id) return null
        return {
            value: params.property_id,
            label: propertyNameFromUrl || propertyNameHint || params.property_id,
        }
    }, [params.property_id, propertyNameFromUrl, propertyNameHint])

    const exportHref = useMemo(() => buildDefaultPaymentLogExportHref(params), [params])

    const openPaymentLogEdit = useCallback((paymentLogId: number) => {
        const path = `/reports/default-payment-logs/edit/${encodeURIComponent(String(paymentLogId))}`
        navigate(path)
    }, [navigate])

    /** Transaction Details — same in-app navigation as the edit (pencil) action. */
    const openPaymentLogView = useCallback((paymentLogId: number) => {
        const path = `/dashboard/reports/default-payment-logs/${encodeURIComponent(String(paymentLogId))}`
        navigate(path)
    }, [navigate])

    /** Same as Angular `showResidentDefault`: defaults list with search = applicant id (past customer). */
    const openDefaultsListForApplicant = useCallback((applicantId: number) => {
        window.open(
            getReimbursementsListUrlForApplicant(applicantId),
            '_blank',
            'noopener,noreferrer',
        )
    }, [])

    const showTotalPaymentStrip =
        meta.total_payment_count != null &&
        String(meta.total_payment_count).trim() !== '' &&
        String(meta.total_payment_count) !== '0'

    return (
        <div data-testid="default-payment-logs-page">
            <PageShell
                title="Default Payment Logs"
                pageHeaderTestIdRoot="default-payment-logs-page-header"
                contentClassName="!space-y-6 w-full max-w-none min-w-0"
            >
                <DefaultPaymentLogsInlineFilterBar
                    values={filterValuesForDrawer}
                    onFiltersChange={applyDrawerFilters}
                    onReset={resetFilters}
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                    isOpen={isFilterOpen}
                    onOpen={() => setIsFilterOpen(true)}
                    onClose={() => setIsFilterOpen(false)}
                    activeFilterCount={activeFilterCount}
                    initialPropertyOption={initialPropertyOption}
                    actions={
                        <>
                            {exportHref ? (
                                <a
                                    data-testid="default-payment-logs-export-link"
                                    href={exportHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={REPORT_EXPORT_BUTTON_CLASS}
                                >
                                    <Download className="h-[18px] w-[18px]" aria-hidden />
                                    Export
                                </a>
                            ) : null}
                            <ListColumnsMenu
                                columns={columnsController}
                                testIdPrefix="default-payment-logs"
                            />
                        </>
                    }
                />

                {showTotalPaymentStrip ? (
                    <ReportOverviewMetrics
                        title="Payment overview"
                        subtitle="Current report totals"
                        testIdPrefix="default-payment-logs"
                        loading={loading}
                        items={[
                            {
                                id: 'total-payments',
                                label: 'Total Payments',
                                value: formatAmount(meta.total_payment_count),
                                icon: <Wallet className="h-6 w-6" aria-hidden />,
                                tone: 'sky',
                            },
                        ]}
                    />
                ) : null}

                <div
                    data-testid="default-payment-logs-results"
                    className={reportStyles.tableCard}
                >
                    <ListPaginationBar
                        placement="header"
                        currentPage={pageOptions.current}
                        lastPage={pageOptions.last}
                        totalRecords={meta.total ?? 0}
                        perPage={params.perPage}
                        onPageChange={(page) => setParams((p) => ({ ...p, page }))}
                        onPerPageChange={(perPage) => setParams((p) => ({ ...p, perPage, page: 1 }))}
                        testIdPrefix="default-payment-logs"
                        barDataTestId="default-payment-logs-pagination-header-bar"
                        summaryLayout="angular"
                    />

                    {loading ? (
                        <div
                            data-testid="default-payment-logs-loading"
                            className="flex min-h-[12rem] items-center justify-center py-16"
                        >
                            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-hidden />
                        </div>
                    ) : (
                        <ReportTableScroll
                            enabled={rows.length > 0}
                            testIdPrefix="default-payment-logs"
                        >
                            <table
                                data-testid="default-payment-logs-table"
                                className={cn(
                                    reportStyles.listingTable,
                                    'w-full border-collapse text-left text-slate-800',
                                    '[&_thead_th]:px-3 [&_tbody_td]:px-3',
                                    '[&_thead_th]:py-2.5 [&_tbody_td]:py-2.5',
                                )}
                            >
                                <thead>
                                    <tr className="normal-case">
                                        {visibleColumns.map((col) => {
                                            const active = params.orderBy === col.key
                                            return (
                                                <th key={col.key} className="text-left">
                                                    <button
                                                        type="button"
                                                        onClick={() => sort(col.key)}
                                                        className={cn(
                                                            'inline-flex max-w-none items-center gap-1.5 whitespace-nowrap text-left font-semibold text-slate-600 hover:text-slate-900',
                                                            active && 'text-[rgb(21_151_255)]',
                                                        )}
                                                    >
                                                        <span>{col.label}</span>
                                                        <span className={cn('inline-flex items-center ml-1 text-[11px]', active ? 'text-[#1597ff]' : 'text-slate-400')}>
                                                            {active ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3 shrink-0" aria-hidden />}
                                                        </span>
                                                    </button>
                                                </th>
                                            )
                                        })}
                                        <th
                                            data-col="action"
                                            className="normal-case !px-2 !pl-1 sm:!pl-1.5"
                                        >
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <TableSearchFilterEmptyTableRow
                                            colSpan={visibleColumns.length + 1}
                                            rowDataTestId="default-payment-logs-table-empty-row"
                                            data-testid="default-payment-logs-table-empty-message"
                                        />
                                    ) : (
                                        rows.map((row, index) => (
                                            <tr
                                                key={
                                                    row.payment_log_id != null
                                                        ? String(row.payment_log_id)
                                                        : `row-${index}`
                                                }
                                                className="border-b border-slate-100"
                                            >
                                                {visibleColumns.map((col) => (
                                                    <td
                                                        key={col.key}
                                                        className={defaultPaymentLogCellClassName(col.key)}
                                                    >
                                                        {renderDefaultPaymentLogCell(
                                                            col.key,
                                                            row,
                                                            {
                                                                onOpenResident: (aid) =>
                                                                    void openDefaultsListForApplicant(aid),
                                                            },
                                                        )}
                                                    </td>
                                                ))}
                                                <td
                                                    data-col="action"
                                                    className="whitespace-nowrap !pl-1 !pr-2 sm:!pl-1.5"
                                                >
                                                    {row.payment_log_id != null ? (
                                                        <div className="inline-flex items-center justify-end gap-1">
                                                            <button
                                                                type="button"
                                                                title="View log"
                                                                aria-label="View payment log"
                                                                data-testid={`default-payment-logs-view-${row.payment_log_id}`}
                                                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-[rgb(21_151_255)]"
                                                                onClick={() =>
                                                                    openPaymentLogView(row.payment_log_id!)
                                                                }
                                                            >
                                                                <Eye className="h-4 w-4" aria-hidden />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                title="Edit payment log"
                                                                aria-label="Edit payment log"
                                                                data-testid={`default-payment-logs-edit-${row.payment_log_id}`}
                                                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-[rgb(21_151_255)]"
                                                                onClick={() =>
                                                                    void openPaymentLogEdit(row.payment_log_id!)
                                                                }
                                                            >
                                                                <Pencil className="h-4 w-4" aria-hidden />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </ReportTableScroll>
                    )}

                    <ListPaginationBar
                        placement="footer"
                        currentPage={pageOptions.current}
                        lastPage={pageOptions.last}
                        totalRecords={meta.total ?? 0}
                        perPage={params.perPage}
                        onPageChange={(page) => setParams((p) => ({ ...p, page }))}
                        onPerPageChange={(perPage) => setParams((p) => ({ ...p, perPage, page: 1 }))}
                        testIdPrefix="default-payment-logs"
                        barDataTestId="default-payment-logs-pagination-footer-bar"
                        summaryLayout="angular"
                    />
                </div>
            </PageShell>
        </div>
    )
}
